// GuideEditorPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import GuideEditor from "../components/GuideEditor";
import ExperiencesApi from "../services/ExperiencesAPI";
import { AppButton } from "../components/ui";
import { fetchFeaturedExperienceTemplates } from "../services/featuredExperiences";


// Helper to generate a new UUID
const generateId = () => crypto.randomUUID();

const createBlankGuide = () => ({
  name: "New Guide",
  id: generateId(),
  insightDatas: [],
  insightDataIds: [],
});

const GuideEditorPage = () => {
  const { guideId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasStartedFromScratch, setHasStartedFromScratch] = useState(false);
  const [featuredTemplates, setFeaturedTemplates] = useState([]);
  const [featuredTemplatesLoading, setFeaturedTemplatesLoading] = useState(false);
  const [featuredTemplatesError, setFeaturedTemplatesError] = useState("");
  const [selectedTemplateExperienceId, setSelectedTemplateExperienceId] = useState("");
  const cloneFrom = searchParams.get("cloneFrom");
  const isNewGuide = guideId === "new";
  const showStartOptions = isNewGuide && !guide;

  const featuredSystemOptions = useMemo(() => {
    const seenSystemIds = new Set();

    return featuredTemplates.reduce((options, template) => {
      if (!template?.systemId || seenSystemIds.has(template.systemId)) {
        return options;
      }

      seenSystemIds.add(template.systemId);
      options.push({
        systemId: template.systemId,
        label: template.systemName || template.title || template.systemId,
      });
      return options;
    }, []);
  }, [featuredTemplates]);

  const selectedTemplate = useMemo(
    () =>
      featuredTemplates.find(
        (template) => template.experienceId === selectedTemplateExperienceId
      ) || null,
    [featuredTemplates, selectedTemplateExperienceId]
  );

  useEffect(() => {
    let cancelled = false;

    const loadGuide = async () => {
      setLoading(true);
      setErrorMessage("");

      if (guideId === "new") {
        console.log("CLONING");

        if (!cloneFrom) {
          if (!cancelled) {
            if (hasStartedFromScratch) {
              setGuide(createBlankGuide());
            } else {
              setGuide(null);
            }
            setLoading(false);
          }
          return;
        }

        try {
          const response = await ExperiencesApi.get(`guides/${cloneFrom}`, {
            requiresAuth: true,
          });
          if (cancelled) {
            return;
          }

          const guideData = response.data;
          const newGuideId = generateId();
          const clonedInsights = cloneInsightData(guideData.insightDatas || []);

          setGuide({
            ...guideData,
            id: newGuideId,
            name: `${guideData.name} (Clone)`,
            insightDatas: clonedInsights,
            insightDataIds: clonedInsights.map((insight) => insight.id),
          });
        } catch (error) {
          console.error("Error fetching guide for clone", error);
          if (!cancelled) {
            setGuide(createBlankGuide());
            setErrorMessage(
              "Unable to load that guide template. Starting from a blank guide instead."
            );
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
        return;
      }

      if (cloneFrom) {
        setSelectedTemplateExperienceId("");
      }

      console.log("DO NORMAL GUIDE");
      try {
        const response = await ExperiencesApi.get(`guides/${guideId}`, {
          requiresAuth: true,
        });
        if (!cancelled) {
          setGuide(response.data);
        }
      } catch (error) {
        console.error("Error fetching guide", error);
        if (!cancelled) {
          setGuide(null);
          setErrorMessage("Unable to load this guide right now.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadGuide();

    return () => {
      cancelled = true;
    };
  }, [cloneFrom, guideId, hasStartedFromScratch]);

  useEffect(() => {
    if (!isNewGuide || cloneFrom) {
      setHasStartedFromScratch(false);
    }
  }, [cloneFrom, isNewGuide]);

  useEffect(() => {
    let cancelled = false;

    if (guideId !== "new" || !showStartOptions) {
      setFeaturedTemplates([]);
      setFeaturedTemplatesError("");
      setSelectedTemplateExperienceId("");
      setFeaturedTemplatesLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadFeaturedTemplates = async () => {
      setFeaturedTemplatesLoading(true);
      setFeaturedTemplatesError("");

      try {
        const templates = await fetchFeaturedExperienceTemplates();
        if (!cancelled) {
          setFeaturedTemplates(templates.filter((template) => template.guideId));
        }
      } catch (error) {
        console.error("Error loading featured guide templates", error);
        if (!cancelled) {
          setFeaturedTemplates([]);
          setFeaturedTemplatesError(
            "Unable to load featured guide templates right now."
          );
        }
      } finally {
        if (!cancelled) {
          setFeaturedTemplatesLoading(false);
        }
      }
    };

    loadFeaturedTemplates();

    return () => {
      cancelled = true;
    };
  }, [guideId, showStartOptions]);

  useEffect(() => {
    console.log("GUIDE", guide);
  }, [guide]);

  const handleUseTemplate = () => {
    if (!selectedTemplate?.guideId) {
      return;
    }

    navigate(`/guides/new?cloneFrom=${selectedTemplate.guideId}`);
  };

  const handleStartFromScratch = () => {
    setErrorMessage("");
    setHasStartedFromScratch(true);

    if (cloneFrom) {
      navigate("/guides/new", { replace: true });
    }
  };


  if (loading) {
    return <CircularProgress />;
  }

  if (!guide && !showStartOptions) {
    return (
      <Box sx={{ padding: 2 }}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      {errorMessage ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      {showStartOptions ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 460px)",
              md: "repeat(2, minmax(320px, 420px))",
            },
            gap: 3,
            alignItems: "stretch",
            justifyContent: "center",
            width: "100%",
            maxWidth: "960px",
            mx: "auto",
            mt: 6,
          }}
        >
          <Paper sx={{ p: 3, width: "100%" }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <div>
                <Typography variant="h6">Use Template</Typography>
                <Typography variant="body2" color="text.secondary">
                  Start from one of the featured experience guides from the home page.
                </Typography>
              </div>

              {featuredTemplatesError ? (
                <Alert severity="warning">{featuredTemplatesError}</Alert>
              ) : null}

              <FormControl fullWidth disabled={featuredTemplatesLoading}>
                <InputLabel id="featured-guide-template-label">
                  Featured Experience Template
                </InputLabel>
                <Select
                  labelId="featured-guide-template-label"
                  value={selectedTemplateExperienceId}
                  label="Featured Experience Template"
                  onChange={(event) =>
                    setSelectedTemplateExperienceId(event.target.value)
                  }
                >
                  {featuredTemplatesLoading ? (
                    <MenuItem value="" disabled>
                      Loading featured templates...
                    </MenuItem>
                  ) : null}
                  {!featuredTemplatesLoading && featuredTemplates.length === 0 ? (
                    <MenuItem value="" disabled>
                      No featured templates available
                    </MenuItem>
                  ) : null}
                  {featuredTemplates.map((template) => (
                    <MenuItem key={template.experienceId} value={template.experienceId}>
                      {template.systemName
                        ? `${template.title} (${template.systemName})`
                        : template.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: "auto" }}>
                <AppButton
                  kind="secondary"
                  onClick={handleUseTemplate}
                  disabled={!selectedTemplate || featuredTemplatesLoading}
                  fullWidth
                >
                  Use Template
                </AppButton>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, width: "100%" }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <div>
                <Typography variant="h6">Start From Scratch</Typography>
                <Typography variant="body2" color="text.secondary">
                  Open a blank guide editor and choose the system and insights yourself.
                </Typography>
              </div>

              <Box sx={{ flexGrow: 1 }} />

              <AppButton kind="primary" onClick={handleStartFromScratch} fullWidth>
                Start From Scratch
              </AppButton>
            </Stack>
          </Paper>
        </Box>
      ) : null}

      {guide ? (
        <GuideEditor
          key={guide.id}
          initialGuide={guide}
          defaultSystemOptions={
            guideId === "new" && featuredSystemOptions.length > 0
              ? featuredSystemOptions
              : undefined
          }
        />
      ) : null}
    </Box>
  );
};


const cloneInsightData = (insightDatas) => {
  const idMapping = {};
  const isWrapped = insightDatas.length && insightDatas[0]?.data; // detect if already wrapped

  // Normalize to plain objects first
  const plain = isWrapped ? insightDatas.map(i => i.data) : insightDatas;

  const clonedPlain = plain.map((insight) => {
    const newId = generateId();
    idMapping[insight.id] = newId;
    return { ...insight, id: newId };
  });

  const updated = clonedPlain.map((insight, idx, arr) => {
    const prevInsightId = idx > 0 ? arr[idx - 1].id : null;

    const updatedTriggers = Array.isArray(insight.insightTriggers)
      ? insight.insightTriggers.map((trigger, i) => {
          const t = { ...trigger };
          if (t.triggerId) t.triggerId = idMapping[t.triggerId] ?? t.triggerId;
          if (i === 0 && t.insightTriggerType === 2 && prevInsightId) {
            t.triggerId = prevInsightId;
            t.name = arr[idx - 1]?.name || t.name;
          }
          return t;
        })
      : insight.insightTriggers;

    const remapConds = (conds) =>
      Array.isArray(conds)
        ? conds.map((c) => ({
            ...c,
            conditions: Array.isArray(c.conditions)
              ? c.conditions.map((x) => idMapping[x] ?? x)
              : c.conditions,
          }))
        : conds;

    return {
      ...insight,
      insightTriggers: updatedTriggers,
      startConditions: remapConds(insight.startConditions),
      endConditions: remapConds(insight.endConditions),
    };
  });

  // Re-wrap only if the input was wrapped
  return isWrapped ? updated.map(i => ({ data: i })) : updated;
};




export default GuideEditorPage;
